/**
 * Utilitaire pour vérifier l'expiration des tokens JWT
 */

/**
 * Décode un token JWT sans vérifier la signature
 * @param {string} token - Le token JWT
 * @returns {object|null} - Le payload décodé ou null si invalide
 */
export function decodeToken(token) {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    return decoded;
  } catch (err) {
    console.error('[Auth] Erreur décodage token:', err);
    return null;
  }
}

/**
 * Vérifie si un token JWT est expiré
 * @param {string} token - Le token JWT
 * @returns {boolean} - true si expiré, false sinon
 */
export function isTokenExpired(token) {
  const decoded = decodeToken(token);

  if (!decoded || !decoded.exp) {
    console.warn('[Auth] Token invalide ou sans date d\'expiration');
    return true; // Considérer comme expiré si impossible à décoder
  }

  const currentTime = Math.floor(Date.now() / 1000); // En secondes
  const isExpired = decoded.exp < currentTime;

  if (isExpired) {
    console.warn('[Auth] ⚠️ Token expiré:', {
      exp: new Date(decoded.exp * 1000),
      now: new Date(currentTime * 1000),
      diff: Math.floor((currentTime - decoded.exp) / 60) + ' minutes'
    });
  }

  return isExpired;
}

/**
 * Vérifie le token et redirige vers login si nécessaire
 * @param {string} tokenKey - La clé du token dans localStorage
 * @param {object} router - L'instance du router Vue
 * @returns {boolean} - true si le token est valide, false sinon
 */
export function checkAuthToken(tokenKey, router) {
  const token = localStorage.getItem(tokenKey);

  if (!token) {
    console.warn('[Auth] ⚠️ Aucun token trouvé');
    return false;
  }

  if (isTokenExpired(token)) {
    console.warn('[Auth] ⚠️ Token expiré, redirection vers login');
    localStorage.removeItem(tokenKey);

    if (router) {
      router.push('/login');
    }

    return false;
  }

  return true;
}

/**
 * Crée un intercepteur pour les réponses HTTP
 * Gère automatiquement les erreurs 401/403
 * @param {string} tokenKey - La clé du token dans localStorage
 * @param {object} router - L'instance du router Vue
 */
export function setupHttpErrorHandler(tokenKey, router) {
  // Sauvegarder la fonction fetch originale
  const originalFetch = window.fetch;

  // Remplacer fetch par une version qui gère les erreurs d'auth
  window.fetch = async function (...args) {
    try {
      const response = await originalFetch(...args);

      // Si 401 ou 403, c'est que le token est invalide/expiré
      if (response.status === 401 || response.status === 403) {
        console.error('[Auth] ❌ Erreur d\'authentification:', response.status);
        localStorage.removeItem(tokenKey);

        if (router && router.currentRoute.value.path !== '/login') {
          router.push('/login');
        }
      }

      return response;
    } catch (error) {
      console.error('[HTTP] Erreur requête:', error);
      throw error;
    }
  };

  console.log('[Auth] ✅ Intercepteur HTTP installé');
}
